/**
 * Why this exists:
 * In local mocked runtime, Mirage/Pretender patches `fetch`/XHR and some CDN PDF responses can be
 * transformed into non-native response/body paths, which caused blank PDF preview/print even when
 * the same URL opened fine directly in the browser.
 *
 * We capture browser-native network functions before `mockServer()` starts, then use them only for
 * PDF binary fetch/print flows so bytes stay unchanged and behavior matches non-mocked environments.
 */

let nativeFetchImpl: typeof fetch | undefined;
let NativeXHR: typeof XMLHttpRequest | undefined;

/** Call once from `main.tsx` before `mockServer()`. */
export function captureNativeNetwork(): void {
  if (typeof window === 'undefined') return;
  if (nativeFetchImpl) return;
  nativeFetchImpl = window.fetch.bind(window);
  NativeXHR = window.XMLHttpRequest;
}

export function nativeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!nativeFetchImpl) captureNativeNetwork();
  const impl =
    nativeFetchImpl ??
    (typeof fetch === 'function'
      ? fetch.bind(typeof window !== 'undefined' ? window : globalThis)
      : undefined);
  if (!impl) throw new Error('No fetch implementation available');
  return impl(input, init);
}

/** Use when XHR must bypass Pretender (same binary guarantees as `nativeFetch`). */
export function createNativeXMLHttpRequest(): XMLHttpRequest {
  if (!NativeXHR) captureNativeNetwork();
  if (!NativeXHR) throw new Error('No XMLHttpRequest implementation available');
  return new NativeXHR();
}
